import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db8crbk6oi3g3tv8uleg";
const CONTACT_EMAIL = "support@sakura-anime.online";

type Status = "idle" | "submitting" | "success" | "error";

export default function About() {
  useSeo(
    "关于我们 - 樱花动漫在线",
    "了解樱花动漫在线的网站定位、联系邮箱与反馈方式。樱花动漫在线是面向动漫爱好者的内容门户，提供动漫浏览、分类筛选与在线播放体验演示。"
  );

  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [formMsg, setFormMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormMsg("感谢你的反馈，我们已收到你的留言。");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请填写完整的姓名、邮箱和反馈内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请输入正确的邮箱地址，方便我们回复你。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        body.append(key, String(value));
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      const isSpam = String(serverMsg || "").toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交失败，请稍后重试。");
        return;
      }

      setStatus("success");
      setFormMsg("感谢你的反馈，我们会认真阅读每一条建议。");
      setFormError("");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常导致提交失败，请检查网络后重试。");
    }
  };

  const values = [
    { icon: "ri-compass-3-line", title: "内容聚合", desc: "汇集热门新番与经典动画，一站浏览不迷路。" },
    { icon: "ri-filter-3-line", title: "精准筛选", desc: "按类型、地区、年代、状态与评分快速定位。" },
    { icon: "ri-heart-3-line", title: "个性收藏", desc: "喜欢的作品一键收藏，本地保存随时回看。" },
    { icon: "ri-play-circle-line", title: "播放体验", desc: "提供前端模拟播放演示，还原观看交互流程。" },
  ];

  return (
    <div className="w-full">
      <div className="w-full bg-primary-50 border-b border-primary-100">
        <div className="w-full px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-600 mb-3 flex-wrap">
            <Link to="/" className="hover:text-primary-700 cursor-pointer">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-900 font-medium">关于我们</span>
          </nav>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950">关于樱花动漫在线</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-700 max-w-3xl leading-relaxed">
            樱花动漫在线是一个专注于动漫内容展示与在线播放体验的二次元门户，致力于让每一位动漫爱好者
            都能用最短的时间找到最想看的那一部作品。
          </p>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-10 md:py-14">
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-4">网站定位</h2>
            <div className="text-sm md:text-base text-foreground-700 leading-relaxed space-y-4">
              <p>
                我们面向热爱动漫的年轻用户群体，围绕「发现—筛选—观看—收藏」的完整路径设计体验。
                首页聚合焦点推荐与最新更新，分类页提供多维筛选，详情页呈现完整信息与模拟播放，
                专题页则用主题化的方式帮你系统性地发掘优质内容。
              </p>
              <p>
                需要说明的是，本站当前为内容展示与交互体验演示型网站，站内所有动漫资料、评分、
                播放与数据均为示例内容，不提供也不存储任何真实视频流资源。我们鼓励大家通过正规渠道
                支持正版作品，共同维护良好的创作环境。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg bg-background-50 border border-background-200 p-5">
                  <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center text-xl mb-3">
                    <i className={v.icon}></i>
                  </span>
                  <h3 className="text-sm font-bold text-foreground-950 mb-1">{v.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg bg-secondary-50 border border-secondary-100 p-6">
              <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-contacts-line text-secondary-700"></i>
                联系方式
              </h2>
              <div className="flex flex-col sm:flex-row gap-6">
                <div>
                  <p className="text-xs text-foreground-500 mb-1">合作与反馈邮箱</p>
                  <p className="text-sm md:text-base font-medium text-foreground-900">{CONTACT_EMAIL}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500 mb-1">响应时间</p>
                  <p className="text-sm md:text-base font-medium text-foreground-900">工作日 1-2 个自然日内</p>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[420px] shrink-0">
            <div className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 lg:sticky lg:top-24">
              <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-1">给我们留言</h2>
              <p className="text-xs md:text-sm text-foreground-600 mb-5 leading-relaxed">
                对网站有任何建议、内容纠错或合作意向，欢迎随时告诉我们。
              </p>

              <form data-readdy-form id="sakura-feedback-form" onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  name="website_alt"
                  className="field-website-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <div>
                  <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    你的称呼 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="fb-name"
                    type="text"
                    name="name"
                    placeholder="请输入你的姓名或昵称"
                    className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱地址 <span className="text-primary-600">*</span>
                  </label>
                  <input
                    id="fb-email"
                    type="email"
                    name="email"
                    placeholder="用于我们回复你"
                    className="w-full h-11 px-3.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400"
                  />
                </div>

                <div>
                  <label htmlFor="fb-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    反馈内容 <span className="text-primary-600">*</span>
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="请描述你的建议或遇到的问题（最多 500 字）"
                    className="w-full px-3.5 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-400 resize-none"
                  ></textarea>
                </div>

                {status === "error" && formError && (
                  <div className="rounded-md bg-primary-50 border border-primary-200 px-3.5 py-3 text-xs md:text-sm text-primary-800 flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{formError}</span>
                  </div>
                )}

                {status === "success" && (
                  <div className="rounded-md bg-secondary-50 border border-secondary-200 px-3.5 py-3 text-xs md:text-sm text-secondary-900 flex items-start gap-2">
                    <i className="ri-checkbox-circle-line mt-0.5"></i>
                    <span>{formMsg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === "submitting" ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      正在提交…
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      提交反馈
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="mt-12 rounded-lg bg-background-100 p-6 md:p-8">
          <h2 className="text-base md:text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-shield-check-line text-foreground-600"></i>
            版权信息与免责声明
          </h2>
          <div className="text-xs md:text-sm text-foreground-600 leading-relaxed space-y-2">
            <p>
              本站为动漫内容展示与交互体验演示网站，站内所有动漫名称、封面、简介、评分与播放内容均为
              虚构或示例素材，仅用于产品界面与功能演示，不构成任何真实资源提供。
            </p>
            <p>
              本站不存储、不提供也不传播任何影视视频文件，播放区域仅为前端模拟交互。若你喜爱某部作品，
              请通过官方与正版渠道观看，支持创作者。
            </p>
            <p>© 2026 樱花动漫在线 · 保留所有权利。</p>
          </div>
        </div>
      </div>
    </div>
  );
}